import { cn } from '@hbs-us/ui';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

// Token-backed text-input classes — the established native-input pattern from
// reports/RangeFilter + invoices/AddPaymentForm (@hbs-us/ui exports no Input).
const INPUT_CLASS =
  'h-11 w-full min-w-24 rounded-md border border-input bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

/**
 * Shared `_ops` block — inline edit-in-place cell (UI-SPEC §4 "Inline edit").
 *
 * Where the legacy app used a whole Edit page to change one field (timesheet
 * correction, inventory qty), the redesign lets the operator click the value →
 * a labelled input appears → Enter saves via the passed `onSave` callback
 * (the caller does the optimistic PATCH + invalidate) → Esc cancels and
 * restores the prior value.
 *
 * Accessibility (A11Y-01): the trigger is a real `<button>` (keyboard
 * activatable), the input has an associated (visually-hidden) `<label>`, both
 * the trigger and the input are 44px-min targets, Enter/Esc are handled, and
 * focus moves into the input on open and back to the trigger on close. Every
 * color is a locked token (no hex, BRAND-06). It does not fetch — `onSave`
 * owns the mutation; while it is pending the input is disabled.
 */

export function InlineEditCell({
  value,
  label,
  onSave,
  type = 'text',
  inputMode,
  pending = false,
  formatDisplay,
  className,
}: {
  /** Current value. */
  value: string;
  /** Accessible label for the edit control (a t() string in app code). */
  label: string;
  /** Save handler — owns the optimistic mutation + invalidate. */
  onSave: (next: string) => void;
  type?: 'text' | 'number';
  inputMode?: 'numeric' | 'decimal' | 'text';
  /** True while the caller's mutation is in flight (locks the input). */
  pending?: boolean;
  /** Optional display formatter for the read state (e.g. currency). */
  formatDisplay?: (value: string) => string;
  className?: string;
}) {
  const inputId = useId();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Keep the draft in sync when the upstream value changes between edits.
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const close = useCallback(() => {
    setEditing(false);
    triggerRef.current?.focus();
  }, []);

  const commit = useCallback(() => {
    const next = draft.trim();
    if (next !== value) onSave(next);
    close();
  }, [draft, value, onSave, close]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commit();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setDraft(value);
        close();
      }
    },
    [commit, value, close],
  );

  if (editing) {
    return (
      <span className={cn('inline-flex items-center', className)}>
        <label htmlFor={inputId} className="sr-only">
          {label}
        </label>
        <input
          ref={inputRef}
          id={inputId}
          type={type}
          inputMode={inputMode}
          value={draft}
          disabled={pending}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commit}
          className={INPUT_CLASS}
        />
      </span>
    );
  }

  return (
    <button
      ref={triggerRef}
      type="button"
      aria-label={label}
      onClick={() => setEditing(true)}
      className={cn(
        'inline-flex h-11 min-w-24 items-center rounded-md px-2 text-left text-foreground text-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        className,
      )}
    >
      {formatDisplay ? formatDisplay(value) : value}
    </button>
  );
}
